import { Button } from 'shared/components/ui';
import React, { useState, useEffect, useRef } from 'react';
import { obrasSocialesAPI } from '../../../core/api/api';
import { facturacionAPI } from '../../facturacion/services/facturacionAPI';
import { Loading } from '../../../shared/components';
import './CargaFacturasOS.css';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;

const CargaFacturasOS = ({ onToast }) => {
  const mesActual = new Date().getMonth() === 0 ? 12 : new Date().getMonth();
  const anioActual = new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear();

  // Estado general
  const [paso, setPaso] = useState(1); // 1: Seleccionar OS, 2: Cargar factura, 3: Confirmar
  const [loading, setLoading] = useState(false);

  // Filtros
  const [mes, setMes] = useState(mesActual);
  const [anio, setAnio] = useState(anioActual);
  const [obrasSociales, setObrasSociales] = useState([]);
  const [obraSocialSeleccionada, setObraSocialSeleccionada] = useState('');
  const [practicas, setPracticas] = useState([]);
  const [totalImporte, setTotalImporte] = useState(0);
  const [cantPracticas, setCantPracticas] = useState(0);

  // Upload
  const [archivo, setArchivo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analizando, setAnalizando] = useState(false);
  const [resultadoAnalisis, setResultadoAnalisis] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    cargarObrasSociales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarObrasSociales = async () => {
    try {
      const response = await obrasSocialesAPI.getObrasSociales();
      setObrasSociales(response.data || []);
    } catch {
      onToast?.({ message: 'Error al cargar obras sociales', type: 'error' });
    }
  };

  // Paso 1: Seleccionar OS y cargar datos
  const handleSeleccionarOS = async () => {
    if (!obraSocialSeleccionada) {
      onToast?.({ message: 'Seleccione una obra social', type: 'error' });
      return;
    }
    setLoading(true);
    try {
      const fecha = new Date(anio, mes - 1, 1);
      const data = await facturacionAPI.getFacturacionObrasSociales(obraSocialSeleccionada, fecha);
      // Solo se liquidan las prácticas facturables. Las quitadas desde la grilla de
      // facturación quedan con facturable === false y deben excluirse de la carga de
      // factura (cantidad, monto e idsTurnos a asignar). Mismo criterio que la grilla.
      const items = (data || []).filter(p => p.facturable !== false);
      setPracticas(items);
      const total = items.reduce((sum, p) => {
        const importe = parseFloat(p.importe) || 0;
        const medioIva = parseFloat(p.medioIva) || 0;
        const conCopago = parseFloat(p.conCopago) || 0;
        return sum + (importe + medioIva - conCopago);
      }, 0);
      setTotalImporte(total);
      setCantPracticas(items.length);
      setPaso(2);
    } catch {
      onToast?.({ message: 'Error al cargar la facturación de la obra social', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Paso 2: Seleccionar archivo
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setArchivo(file);
    setResultadoAnalisis(null);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleAnalizar = async () => {
    if (!archivo) {
      onToast?.({ message: 'Seleccione un archivo primero', type: 'error' });
      return;
    }

    setAnalizando(true);
    setResultadoAnalisis(null);
    try {
      const resultado = await facturacionAPI.analizarFacturaOS(archivo);
      if (resultado.success && resultado.data) {
        setResultadoAnalisis({ ...resultado.data, archivo: resultado.archivo });
        setPaso(3);

        const osDetectadaId = resultado.data.idObraSocialDetectada;
        if (osDetectadaId && String(osDetectadaId) !== String(obraSocialSeleccionada)) {
          // La advertencia visual ya es suficiente
        } else {
          onToast?.({ message: 'Factura analizada correctamente', type: 'success' });
        }
      } else {
        onToast?.({ message: 'No se pudo extraer información de la factura', type: 'error' });
      }
    } catch (err) {
      onToast?.({ message: err.response?.data?.message || 'Error al analizar la factura', type: 'error' });
    } finally {
      setAnalizando(false);
    }
  };

  // Paso 3: Confirmar y guardar
  const handleGuardarNroFactura = async () => {
    const nro = resultadoAnalisis?.nroFactura;
    if (!nro || nro <= 0) {
      onToast?.({ message: 'El número de factura detectado no es válido', type: 'error' });
      return;
    }

    setGuardando(true);
    try {
      const idsTurnos = practicas.map(p => p.idTurno);
      await facturacionAPI.confirmarFacturaOS({
        idObraSocial: parseInt(obraSocialSeleccionada),
        nroFactura: nro,
        mes,
        anio,
        archivo: resultadoAnalisis.archivo,
        cuit: resultadoAnalisis.cuit || null,
        razonSocial: resultadoAnalisis.razonSocial || null,
        tipoComprobante: resultadoAnalisis.tipoComprobante || null,
        puntoVenta: resultadoAnalisis.puntoVenta || null,
        idsTurnos
      });
      onToast?.({ message: `Nro. Factura ${nro} asignado a ${idsTurnos.length} registros y archivo guardado`, type: 'success' });
      setArchivo(null);
      setPreview(null);
      setResultadoAnalisis(null);
      setPaso(1);
    } catch {
      onToast?.({ message: 'Error al confirmar la factura', type: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  const handleReset = () => {
    setPaso(1);
    setObraSocialSeleccionada('');
    setPracticas([]);
    setTotalImporte(0);
    setCantPracticas(0);
    setArchivo(null);
    setPreview(null);
    setResultadoAnalisis(null);
  };

  const osSeleccionada = obrasSociales.find(os => os.idObraSocial === parseInt(obraSocialSeleccionada));
  const osNombre = osSeleccionada?.nombre || osSeleccionada?.obraSocial || '';

  return (
    <div className="carga-facturas-os">
      {/* Progress Steps */}
      <div className="cfos-steps">
        <div className={`cfos-step ${paso >= 1 ? 'active' : ''} ${paso > 1 ? 'completed' : ''}`}>
          <div className="cfos-step-number">1</div>
          <span>Seleccionar OS</span>
        </div>
        <div className="cfos-step-line" />
        <div className={`cfos-step ${paso >= 2 ? 'active' : ''} ${paso > 2 ? 'completed' : ''}`}>
          <div className="cfos-step-number">2</div>
          <span>Cargar factura</span>
        </div>
        <div className="cfos-step-line" />
        <div className={`cfos-step ${paso >= 3 ? 'active' : ''}`}>
          <div className="cfos-step-number">3</div>
          <span>Confirmar</span>
        </div>
      </div>

      {loading && <Loading />}

      {/* Paso 1: Seleccionar OS */}
      {paso === 1 && !loading && (
        <div className="cfos-card">
          <div className="cfos-card-icon">🏥</div>
          <h3>Seleccionar Obra Social</h3>
          <p>Elegí la obra social y el período para cargar la factura correspondiente.</p>
          <div className="cfos-filtros-inline">
            <div className="cfos-filtro cfos-filtro-large">
              <label>Obra Social</label>
              <select value={obraSocialSeleccionada} onChange={e => setObraSocialSeleccionada(e.target.value)}>
                <option value="">Seleccione...</option>
                {obrasSociales.map(os => (
                  <option key={os.idObraSocial} value={os.idObraSocial}>{os.obraSocial}</option>
                ))}
              </select>
            </div>
            <div className="cfos-filtro">
              <label>Mes</label>
              <select value={mes} onChange={e => setMes(parseInt(e.target.value))}>
                {MESES.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div className="cfos-filtro">
              <label>Año</label>
              <select value={anio} onChange={e => setAnio(parseInt(e.target.value))}>
                {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <div className="cfos-actions">
            <Button className="cfos-btn-primary" onClick={handleSeleccionarOS}>
              Buscar y continuar →
            </Button>
          </div>
        </div>
      )}

      {/* Paso 2: Cargar factura */}
      {paso === 2 && !loading && (
        <div className="cfos-card">
          <div className="cfos-card-header-info">
            <span className="cfos-badge">{osNombre}</span>
            <span className="cfos-badge cfos-badge-outline">{MESES[mes-1]} {anio}</span>
            <span className="cfos-badge cfos-badge-outline">{cantPracticas} prácticas</span>
          </div>

          {/* Monto de referencia */}
          <div className="cfos-monto-referencia">
            <span className="cfos-monto-label">Monto a facturar</span>
            <span className="cfos-monto-valor">{fmt(totalImporte)}</span>
          </div>

          <h3>Cargar factura</h3>
          <p>
            Subí una imagen o PDF de la factura. El sistema va a leer el número de factura 
            y los datos del emisor para vincularlo con esta obra social.
          </p>

          <div 
            className={`cfos-dropzone ${archivo ? 'cfos-dropzone-active' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              if (file) {
                setArchivo(file);
                setResultadoAnalisis(null);
                if (file.type.startsWith('image/')) {
                  const reader = new FileReader();
                  reader.onload = (ev) => setPreview(ev.target.result);
                  reader.readAsDataURL(file);
                } else {
                  setPreview(null);
                }
              }
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            {archivo ? (
              <div className="cfos-file-info">
                {preview && <img src={preview} alt="Preview" className="cfos-preview" />}
                <div className="cfos-file-details">
                  <span className="cfos-file-name">📄 {archivo.name}</span>
                  <span className="cfos-file-size">{(archivo.size / 1024).toFixed(1)} KB</span>
                </div>
                <Button className="cfos-btn-remove" onClick={(e) => { e.stopPropagation(); setArchivo(null); setPreview(null); setResultadoAnalisis(null); }}>
                  ✕
                </Button>
              </div>
            ) : (
              <div className="cfos-dropzone-content">
                <div className="cfos-dropzone-icon">📁</div>
                <p><strong>Hacé clic o arrastrá</strong> la factura aquí</p>
                <span>Formatos aceptados: JPG, PNG, PDF</span>
              </div>
            )}
          </div>

          <div className="cfos-actions">
            <Button className="cfos-btn-secondary" onClick={() => setPaso(1)}>← Volver</Button>
            <Button
              className="cfos-btn-primary" 
              onClick={handleAnalizar}
              disabled={!archivo || analizando}
            >
              {analizando ? '🔍 Analizando...' : '🔍 Analizar factura'}
            </Button>
          </div>

          {analizando && (
            <div className="cfos-analyzing">
              <div className="cfos-spinner" />
              <p>Analizando la factura...</p>
              <span>Esto puede tardar unos segundos</span>
            </div>
          )}
        </div>
      )}

      {/* Paso 3: Resultado y confirmación */}
      {paso === 3 && !loading && resultadoAnalisis && (
        <div className="cfos-card">
          <div className="cfos-card-header-info">
            <span className="cfos-badge">{osNombre}</span>
            <span className="cfos-badge cfos-badge-outline">{MESES[mes-1]} {anio}</span>
            <span className="cfos-badge cfos-badge-outline">{fmt(totalImporte)}</span>
          </div>

          <h3>✅ Factura analizada</h3>
          <p>Se extrajeron los siguientes datos de la factura:</p>

          {/* Alerta si la OS detectada no coincide */}
          {resultadoAnalisis.idObraSocialDetectada && String(resultadoAnalisis.idObraSocialDetectada) !== String(obraSocialSeleccionada) && (
            <div className="cfos-alerta">
              ⚠️ <strong>Atención:</strong> La factura parece pertenecer a 
              <strong> {resultadoAnalisis.nombreObraSocialDetectada}</strong>, pero seleccionaste 
              <strong> {osNombre}</strong>. Verificá que sea correcto antes de confirmar.
            </div>
          )}

          {resultadoAnalisis.idObraSocialDetectada && String(resultadoAnalisis.idObraSocialDetectada) === String(obraSocialSeleccionada) && (
            <div className="cfos-alerta-ok">
              ✅ La obra social de la factura coincide con la seleccionada: <strong>{osNombre}</strong>
            </div>
          )}

          {/* Comparación de importes */}
          {resultadoAnalisis.importeTotal != null && (
            <div className="cfos-comparacion-importes">
              <div className="cfos-importe-col">
                <span className="cfos-importe-label">Importe factura</span>
                <span className="cfos-importe-valor">{fmt(resultadoAnalisis.importeTotal)}</span>
              </div>
              <div className="cfos-importe-vs">vs</div>
              <div className="cfos-importe-col">
                <span className="cfos-importe-label">Importe sistema</span>
                <span className="cfos-importe-valor">{fmt(totalImporte)}</span>
              </div>
              <div className="cfos-importe-col">
                <span className="cfos-importe-label">Diferencia</span>
                <span className={`cfos-importe-valor ${Math.abs(resultadoAnalisis.importeTotal - totalImporte) > 1 ? 'cfos-importe-alerta' : 'cfos-importe-ok'}`}>
                  {fmt(resultadoAnalisis.importeTotal - totalImporte)}
                </span>
              </div>
            </div>
          )}

          {resultadoAnalisis.importeTotal != null && Math.abs(resultadoAnalisis.importeTotal - totalImporte) > 1 && (
            <div className="cfos-alerta">
              ⚠️ El importe de la factura no coincide con el monto del sistema. Verificá antes de confirmar.
            </div>
          )}

          <div className="cfos-resultado-grid">
            <div className="cfos-resultado-item cfos-resultado-destacado">
              <label>Nro. Factura</label>
              <strong>{resultadoAnalisis.nroFactura ?? 'No detectado'}</strong>
            </div>
            <div className="cfos-resultado-item">
              <label>OS Detectada</label>
              <span>{resultadoAnalisis.nombreObraSocialDetectada ?? resultadoAnalisis.destinatario ?? 'No detectada'}</span>
            </div>
            <div className="cfos-resultado-item">
              <label>CUIT Emisor</label>
              <span>{resultadoAnalisis.cuit ?? 'No detectado'}</span>
            </div>
            <div className="cfos-resultado-item">
              <label>Razón Social</label>
              <span>{resultadoAnalisis.razonSocial ?? 'No detectado'}</span>
            </div>
            <div className="cfos-resultado-item">
              <label>Fecha Emisión</label>
              <span>{resultadoAnalisis.fechaEmision ?? 'No detectado'}</span>
            </div>
            <div className="cfos-resultado-item">
              <label>Tipo Comprobante</label>
              <span>{resultadoAnalisis.tipoComprobante ?? 'No detectado'}</span>
            </div>
            <div className="cfos-resultado-item">
              <label>Punto de Venta</label>
              <span>{resultadoAnalisis.puntoVenta ?? 'No detectado'}</span>
            </div>
            <div className="cfos-resultado-item">
              <label>CUIT Destinatario</label>
              <span>{resultadoAnalisis.cuitDestinatario ?? 'No detectado'}</span>
            </div>
          </div>

          <div className="cfos-confirmacion">
            <p>
              Se asignará el <strong>Nro. Factura {resultadoAnalisis.nroFactura}</strong> a 
              los <strong>{cantPracticas} registros</strong> de <strong>{osNombre}</strong> del 
              período {MESES[mes-1]} {anio}. El archivo quedará guardado en el servidor.
            </p>
          </div>

          <div className="cfos-actions">
            <Button className="cfos-btn-secondary" onClick={() => setPaso(2)}>
              ← Volver a cargar
            </Button>
            <Button
              className="cfos-btn-success" 
              onClick={handleGuardarNroFactura}
              disabled={
                guardando || 
                !resultadoAnalisis.nroFactura ||
                (resultadoAnalisis.idObraSocialDetectada && String(resultadoAnalisis.idObraSocialDetectada) !== String(obraSocialSeleccionada)) ||
                (resultadoAnalisis.importeTotal != null && Math.abs(resultadoAnalisis.importeTotal - totalImporte) > 1)
              }
            >
              {guardando ? '💾 Guardando...' : '✅ Confirmar'}
            </Button>
          </div>
        </div>
      )}

      {/* Botón reset general */}
      {paso > 1 && (
        <div className="cfos-reset">
          <Button className="cfos-btn-reset" onClick={handleReset}>
            ↺ Empezar de nuevo
          </Button>
        </div>
      )}
    </div>
  );
};

export default CargaFacturasOS;
